import { lazy, Suspense, useEffect, useState } from 'react';
import { api, type League, type Season, setAdminLeague } from './lib/api';
import { recordVisit } from './lib/track';
import { homeLeague } from './lib/host';
import { homeRedirect, parseRoute } from './lib/route';
import { dismissSplash } from './lib/splash';
import Platform from './pages/Platform';
import Standings from './pages/Standings';

/*
 * EVERY PAGE BUT THE TWO LANDING ONES IS FETCHED WHEN IT IS OPENED.
 *
 * The app shipped as a single 439 KB chunk, so a player opening the table
 * downloaded all four leagues' scoring screens, the admin console and the
 * poster renderers before anything appeared. Four of these five scoring
 * screens can never be reached in a given league, and most visitors reach none
 * of them.
 *
 * Standings and Platform stay eager because they ARE the first screen — one
 * for a league's address, one for the platform's — and making the landing page
 * cost an extra round trip would spend on arrival exactly what this is saving.
 */
const Fixtures = lazy(() => import('./pages/Fixtures'));
const Match = lazy(() => import('./pages/Match'));
const Mvp = lazy(() => import('./pages/Mvp'));
const Interest = lazy(() => import('./pages/Interest'));
const OwnerInterest = lazy(() => import('./pages/OwnerInterest'));
const SquadEntry = lazy(() => import('./pages/SquadEntry'));

const Player = lazy(() => import('./pages/Player'));
const Teams = lazy(() => import('./pages/Teams'));
const Score = lazy(() => import('./pages/Score'));
const VitalliScore = lazy(() => import('./pages/VitalliScore'));
const PearlsScore = lazy(() => import('./pages/PearlsScore'));
const InterclubScore = lazy(() => import('./pages/InterclubScore'));
const RooftopScore = lazy(() => import('./pages/RooftopScore'));
const Display = lazy(() => import('./pages/Display'));
const Admin = lazy(() => import('./pages/Admin'));
const Captain = lazy(() => import('./pages/Captain'));
const PostMatch = lazy(() => import('./pages/PostMatch'));
import InstallPrompt from './components/InstallPrompt';
import SponsorStrip from './components/SponsorStrip';
import InstagramLink from './components/InstagramLink';
import { ballotHasTab } from './lib/mvp/phase';

/**
 * 8Ball Padel — a platform hosting independent padel leagues.
 *
 * Routes:
 *   #/                       the platform landing page
 *   #/l/<league>             that league's table
 *   #/l/<league>/fixtures    and so on
 *
 * Each league is entered by its own slug, so a link opens straight into that
 * league. Nothing about the shell is specific to any one of them.
 */
function useRoute(): string {
  const [route, setRoute] = useState(() => window.location.hash.slice(1) || '/');
  useEffect(() => {
    const onChange = () => setRoute(window.location.hash.slice(1) || '/');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

/**
 * The four sections, as a bar pinned to the bottom of the screen.
 *
 * Bottom rather than top because the top of an installed app sits under the
 * notch and out of thumb reach — the same thing that made "All leagues"
 * unreachable. Four real pages and no "More": a tab that leads nowhere is
 * worse than no tab bar, and Admin stays visible so "go to Admin" remains one
 * tap from wherever the scorer happens to be.
 *
 * The third label comes from the league, so Society reads Teams and Vitalli
 * reads Houses.
 */
const NAV_ICONS: Record<string, string> = {
  '': 'M3 4h18v16H3zM3 10h18M9 10v10',
  fixtures: 'M3 5h18v16H3zM8 3v4M16 3v4M3 11h18',
  // A crest, not a trophy — at 21px a trophy reads as a wine glass, and both
  // leagues badge their sides anyway: Society's teams and Vitalli's houses.
  teams: 'M12 3l8 3.5v5c0 4.6-3.3 8.6-8 9.5-4.7-.9-8-4.9-8-9.5v-5z',
  // A padlock, since the crest is taken and this section is the one that asks
  // for a password.
  admin: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  // A rosette: the night's award is what this page leads on.
  'post-match': 'M12 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10zM9 13l-2 8 5-3 5 3-2-8',
  // A star, deliberately not a second rosette — this sits beside Night on
  // Pearls' bar and the two awards must not read as the same thing.
  mvp: 'M12 3.5l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.8l6.1-.7z',
};

/**
 * Marks a league swaps for its own, keyed by theme.
 *
 * The defaults suit a crest-and-rosette league. Premier Pearls is a ladies'
 * league that chose gemstones and rose gold from its name down — six teams
 * named for stones, a tiara in the mark — so a shield reads as somebody else's
 * league on their tab bar. A brilliant-cut diamond and a sparkle carry the same
 * two meanings in their own language.
 *
 * A map rather than a branch, so the next league to want its own marks adds a
 * line here instead of an `if` in the render.
 */
const THEME_ICONS: Record<string, Record<string, string>> = {
  pearls: {
    // A brilliant cut seen face on: table across the top, crown facets down to
    // the point. The inner strokes are what stop it reading as a plain kite.
    teams: 'M8 3h8l4 6-8 12L4 9zM8 3 4 9m4-6 4 6 4-6M4 9h16m-8 12L8 9m4 12 4-12',
    // A four-pointed sparkle with a smaller one beside it — the shorthand for
    // a night worth remembering.
    'post-match': 'M11 3c0 4 2 6 6 7-4 1-6 3-6 7-0-4-2-6-6-7 4-1 6-3 6-7zM18.5 14c0 1.8.9 2.7 2.5 3-1.6.3-2.5 1.2-2.5 3 0-1.8-.9-2.7-2.5-3 1.6-.3 2.5-1.2 2.5-3z',
  },
};

/**
 * Whether a season holds what a night summary is made of.
 *
 * The summary reads every game of a gameweek — the pairs, the sets, the
 * breaker. Society's archived Season 1 was recorded under the old scoring,
 * which kept how many sets a pair won and nothing inside them, so there is
 * nothing to summarise and the tab is not offered for it.
 *
 * A property of the season rather than of the league: Society's Season 2
 * records every scoreline, and the tab appears for it the day it opens without
 * anyone remembering to switch something on.
 */
const recordsGameDetail = (scoringSystem?: string) =>
  Boolean(scoringSystem) && scoringSystem !== 'legacy_season1';

/**
 * A fifth tab only where the league has opted in and the season on screen has
 * the detail to fill it.
 */
/** A date from the API, or null — `new Date(null)` is 1970, not "unset". */
const asDate = (iso: string | null | undefined): Date | null => (iso ? new Date(iso) : null);

const navItems = (unitNoun: string, postMatch: boolean, mvp: boolean) => [
  { path: '', label: 'Table' },
  { path: 'fixtures', label: 'Fixtures' },
  ...(postMatch ? [{ path: 'post-match', label: 'Night' }] : []),
  { path: 'teams', label: unitNoun },
  /*
   * The ballot, for the weeks it is running.
   *
   * IT HAD NO WAY IN AT ALL. The page existed and worked; nothing anywhere
   * linked to it. The only route was the organiser pasting a URL to all 62
   * players, and the admin card printed that URL only once voting was already
   * open — so she could not even copy it while setting the thing up.
   *
   * Conditional for the same reason Night is: a league that holds no such
   * award should not carry a tab for it, and six tabs on a phone is already
   * the most this bar should hold. It appears when a season has nominees or
   * dates and goes when the season has neither.
   */
  ...(mvp ? [{ path: 'mvp', label: 'MVP' }] : []),
  { path: 'admin', label: 'Admin' },
];

export default function App() {
  const route = useRoute();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .leagues()
      .then((data) => setLeagues(data.leagues))
      .catch((e: Error) => setError(e.message));
  }, []);

  // #/l/<slug>/<section>/<param>, or #/s/<token> for a captain's link.
  const { inLeague, leagueSlug, section, param, captainToken } = parseRoute(route);

  const league = leagues.find((l) => l.slug === leagueSlug) ?? null;

  /*
   * Whether this address belongs to a league rather than to the platform.
   *
   * A league may bring its own domain, and on it the platform is not merely
   * hidden but absent: the address opens the league, there is no way back to a
   * list of leagues, and no other league can be reached.
   */
  const home = homeLeague(window.location.hostname, leagues);
  // Whether the answer above is known yet, as against merely being null because
  // nothing has loaded.
  const hostSettled = leagues.length > 0 || error !== null;

  /*
   * Which league's admin password the page should use, set before anything
   * below renders. A plain assignment rather than an effect: children read the
   * password while they render, and an effect runs after they already have.
   */
  setAdminLeague(leagueSlug ?? '');

  // Each league keeps its own identity; the platform pages use 8Ball's.
  useEffect(() => {
    document.documentElement.dataset.theme = league?.theme ?? 'platform';
  }, [league?.theme]);

  /*
   * On a league's own domain the tab should say the league, not the platform —
   * it is what a captain sees in their tab strip and what a bookmark is named.
   */
  useEffect(() => {
    document.title = home
      ? `${home.name} — fixtures, results and standings`
      : '8Ball Padel — The Home of Padel Leagues';
  }, [home]);

  /*
   * A league's own domain opens that league.
   *
   * Redirected rather than rendered in place, so that every link below stays
   * `#/l/<slug>` and nothing else in the app has to know this happened.
   *
   * WHERE TO GO IS DECIDED BY homeRedirect, not here, because this effect runs
   * on every render regardless of which branch below is taken — the captain
   * page's early return cannot stop it. See src/lib/route.ts.
   */
  const redirect = homeRedirect({ inLeague, leagueSlug, section, param, captainToken }, home);
  useEffect(() => {
    if (redirect) window.location.replace(redirect);
  }, [redirect]);

  /*
   * The one state in which this component draws nothing at all.
   *
   * It is the wait below on the platform's own page: until the API says whose
   * domain this is, rendering the 8Ball landing page would flash 8Ball's
   * branding on a league's own address. Every other branch puts something on
   * screen — a league's shell with a "Loading…" card, or a card explaining
   * what went wrong.
   *
   * Named here, above the early returns, so the effect below can be a hook.
   *
   * A CAPTAIN'S LINK IS NEVER BLANK. It draws its own page and needs nothing
   * from the leagues call to do it — and on a league's own domain `home` is
   * set, which without this would hold the splash over the captain page for
   * good. Same shape of mistake as the redirect above: a rule written for the
   * platform's front door, applied to a route that is not a front door.
   */
  const blank = !inLeague && !captainToken && (!hostSettled || Boolean(home));

  /*
   * Hand over from the loading screen the moment there is something behind it.
   *
   * Tied to `blank` rather than to mounting. React mounts as soon as the bundle
   * runs, which is BEFORE the API has answered — dismissing there would take
   * the splash down and put an empty screen back in its place for the length of
   * that round trip, which is the second of the two stalls this is fixing.
   */
  useEffect(() => {
    if (!blank) dismissSplash();
  }, [blank]);

  /*
   * Count the visit.
   *
   * Here rather than in each page, because every route in the app passes
   * through this component and a page added later would otherwise have to
   * remember to count itself. Above the early returns below for the same
   * reason: the platform landing page and an unknown slug both leave before
   * those, and both are visits.
   */
  useEffect(() => {
    recordVisit(route);
  }, [route]);

  const [seasonId, setSeasonId] = useState<number | undefined>();

  // Default to the league's current season whenever the league changes.
  useEffect(() => {
    if (!league) {
      setSeasonId(undefined);
      return;
    }
    const current = league.seasons.find((s) => !s.archived) ?? league.seasons[0];
    setSeasonId((existing) =>
      existing && league.seasons.some((s) => s.id === existing) ? existing : current?.id,
    );
  }, [league]);

  const season: Season | undefined = league?.seasons.find((s) => s.id === seasonId);
  const hasNightTab =
    league?.showsPostMatch === true && recordsGameDetail(season?.scoringSystem);

  /*
   * Derived here with the very function the ballot page and the admin card
   * use, rather than read from a flag on the response. A tab that decided for
   * itself whether a vote was running would eventually offer one that the page
   * behind it says is shut.
   */
  /*
   * Two conditions, and both are needed.
   *
   * `runsMvp` is whether the league holds the award at all; ballotHasTab is
   * whether this season's ballot has reached a state worth a tab. A league
   * that does not run it never shows one, however its dates happen to read.
   */
  const hasMvpTab = league?.runsMvp === true && ballotHasTab(
    {
      nominationsClose: asDate(season?.mvp?.nominationsClose),
      votingOpens: asDate(season?.mvp?.votingOpens),
      votingCloses: asDate(season?.mvp?.votingCloses),
    },
    season?.mvp?.nominees ?? 0,
    new Date(),
  );

  /*
   * A captain's link: #/s/<token>, outside the league app entirely.
   *
   * The link carries its own league in the token, so a captain who opens it on
   * the platform address must not be redirected to a league homepage — and one
   * who opens it on their own league's domain must not be turned away for
   * having no league slug in the address.
   *
   * This early return is NOT what keeps the domain rules off it. Both of those
   * rules live in hooks above, which run on every render no matter which branch
   * is taken; each names captainToken itself. An early return cannot un-run an
   * effect, and assuming it could is what made these links open the homepage.
   */
  if (captainToken) {
    return (
      <Suspense fallback={<div className="captain" />}>
        <Captain token={captainToken} />
      </Suspense>
    );
  }

  if (!inLeague) {
    /*
     * The platform's own homepage waits until we know whose address this is.
     *
     * It is a full 8Ball marketing page, so rendering it first and correcting
     * afterwards would flash 8Ball's branding on a league's own domain — the
     * single thing that having a domain is meant to prevent. The wait is one
     * round trip, on the one page that cannot list a league without it anyway.
     */
    if (!hostSettled || home) return <div className="wrap" />;
    return <Platform />;
  }

  /*
   * A league's domain serves that league and refuses the rest.
   *
   * Not merely a default: another league's slug typed or linked into this
   * address is turned away rather than quietly served under this league's
   * banner. A visitor on the Society's site should never find themselves
   * reading Premier Pearls' table.
   */
  if (home && leagueSlug !== home.slug) {
    return (
      <div className="wrap">
        <div className="card">
          <p className="muted">
            This is {home.name}. Nothing else is published at this address.
          </p>
          <a className="btn ghost" href={`#/l/${home.slug}`}>← Back to {home.name}</a>
        </div>
      </div>
    );
  }

  if (leagues.length > 0 && !league) {
    return (
      <div className="wrap">
        <div className="card">
          <p className="muted">No league found at “{leagueSlug}”.</p>
          {!home && <a className="btn ghost" href="#/">← All leagues</a>}
        </div>
      </div>
    );
  }

  // Everything below is a question about one league's season, and none of it can
  // be asked until we know which season that is. Asked without one, the API
  // answers with the newest season on the platform — whichever league it happens
  // to belong to — which is how Vitalli's table appeared under Society's crest
  // on a cold load, then corrected itself once the real request came back.
  //
  // Holding the content here rather than guarding each page means the next page
  // added cannot reintroduce it. It matters most for `score`: without a resolved
  // season, the scoring screen falls through to Society's engine, and that is
  // the one screen that must never be wrong.
  let page: JSX.Element;
  if (league && league.seasons.length === 0) {
    page = (
      <section className="card">
        <p className="muted">No seasons yet for {league.name}.</p>
      </section>
    );
  } else if (!league || !seasonId) {
    page = (
      <section className="card">
        <p className="muted">Loading…</p>
      </section>
    );
  } else if (section === 'fixtures')
    page = (
      <Fixtures
        seasonId={seasonId}
        scoringSystem={season?.scoringSystem}
        league={league}
        seasonLogoUrl={season?.logoUrl}
        startsOn={season?.startsOn}
        seasonName={season?.name}
      />
    );
  else if (section === 'teams')
    page = (
      <Teams
        seasonId={seasonId}
        league={league}
        scoringSystem={season?.scoringSystem}
        seasonName={season?.name}
      />
    );
  else if (section === 'match')
    /* The league and its season badge ride along so the fixture's own page can
       offer the team-sheets poster once both sixes are public — see Match.tsx. */
    page = (
      <Match fixtureId={Number(param)} league={league} seasonLogoUrl={season?.logoUrl} />
    );
  else if (section === 'score')
    // Route to the league's own scoring screen, and so its own engine. Named
    // one by one rather than falling through to Society's: a league whose
    // screen is not built yet must be told so, not handed the wrong one and
    // left to discover it at the end of a match night.
    page =
      season?.scoringSystem === 'vitalli' ? (
        <VitalliScore fixtureId={Number(param)} />
      ) : season?.scoringSystem === 'pearls' ? (
        <PearlsScore fixtureId={Number(param)} league={league} seasonLogoUrl={season?.logoUrl} />
      ) : season?.scoringSystem === 'rooftop' ? (
        // Which screen a Norwood fixture gets depends on the fixture, not the
        // season: Lucky Draw Night is played under different rules inside the
        // same season, and the screen decides once it has loaded the fixture.
        <RooftopScore fixtureId={Number(param)} league={league} seasonLogoUrl={season?.logoUrl} />
      ) : season?.scoringSystem === 'interclub' ? (
        <InterclubScore fixtureId={Number(param)} league={league} seasonLogoUrl={season?.logoUrl} />
      ) : season?.scoringSystem === 'legacy_season1' || season?.scoringSystem === 'season2' ? (
        <Score fixtureId={Number(param)} league={league} seasonLogoUrl={season?.logoUrl} />
      ) : (
        <section className="card">
          <p className="muted">
            {league.name}'s scoring screen is still being built. Nothing can be entered here yet —
            no other league's rules will be used in its place.
          </p>
          <a className="back" href={`#/l/${leagueSlug}/fixtures`}>← Fixtures</a>
        </section>
      );
  // Only where the league has one. Typed by hand into a league without it, the
  // URL falls back to the table rather than rendering a night summary of a
  // season that does not record its nights.
  else if (section === 'post-match')
    page = hasNightTab ? (
      <PostMatch
        seasonId={seasonId}
        scoringSystem={season?.scoringSystem}
        league={league}
        seasonLogoUrl={season?.logoUrl}
      />
    ) : (
      <Standings seasonId={seasonId} league={league} seasonLogoUrl={season?.logoUrl} />
    );
  /* The Most Valued Player ballot, filled in by the players themselves. */
  /*
   * The intake. A plain link rather than a tab: it is for people who are NOT
   * members, so it belongs in a message and a bio, not in the bar a member
   * navigates with. The page itself says when the league is not taking names.
   */
  else if (section === 'join') page = <Interest league={league} />;
  // Buying a team: a link the league sends, like /join. The page says when the
  // league offers no ownership.
  else if (section === 'owners') page = <OwnerInterest league={league} />;
  // A team owner entering a whole squad for a coming season (Society Season 3).
  else if (section === 'squad') page = <SquadEntry league={league} />;
  else if (section === 'mvp')
    // The page too, not only the tab: a link to /mvp on a league that holds no
    // such award should say so rather than draw an empty ballot.
    page = league?.runsMvp ? (
      <Mvp seasonId={seasonId} league={league} />
    ) : (
      <section className="card">
        <div className="card-head"><h2>Most Valued Player</h2></div>
        <p className="muted">{league?.name ?? 'This league'} does not run this award.</p>
      </section>
    );
  /* A player's own season. Public and season-scoped — see Player.tsx. */
  else if (section === 'player')
    page = <Player playerId={Number(param)} seasonId={seasonId} league={league} />;
  else if (section === 'admin') page = <Admin seasonId={seasonId} league={league} />;
  else page = <Standings seasonId={seasonId} league={league} seasonLogoUrl={season?.logoUrl} />;

  const base = `#/l/${leagueSlug}`;

  /*
   * The venue screen, before any of the app's own furniture is drawn.
   *
   * Returned early rather than as another `page`, because everything below —
   * the crumb, the header, the season picker, the sponsors, the tab bar — is
   * exactly what this view exists without. Read from across a room, a person
   * takes in about six things, and every control on screen is one of them
   * spent on something nobody at the venue can tap.
   */
  if (section === 'display') {
    return <Display seasonId={seasonId} league={league} season={season} />;
  }

  return (
    <div className="wrap">
      {!home && <a className="platform-crumb" href="#/">← All leagues</a>}

      <header className="site-head">
        {season?.logoUrl || league?.logoUrl ? (
          <>
            <img
              className="brand-logo"
              src={(season?.logoUrl ?? league?.logoUrl) as string}
              alt={`${league?.name} — ${season?.name ?? ''}`}
            />
            <h1 className="visually-hidden">{league?.name}</h1>
          </>
        ) : (
          <h1>{league?.name ?? 'League'}</h1>
        )}
        {/* The league's own one-liner, so a league without artwork still has a voice. */}
        {league?.tagline && <p className="league-tagline">{league.tagline}</p>}
        {/* The league's own account, under its own name. Players who follow the
            table are the same people who follow the league. */}
        {league?.instagram && (
          <p className="league-social">
            <InstagramLink handle={league.instagram} name={league.name} showHandle />
          </p>
        )}
      </header>


      {(league?.seasons.length ?? 0) > 1 && (
        <div className="season-bar">
          <label htmlFor="season">Season</label>
          <select id="season" value={seasonId ?? ''} onChange={(e) => setSeasonId(Number(e.target.value))}>
            {league?.seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {season?.archived && <span className="badge">Archived</span>}
        </div>
      )}

      {error && <div className="card"><p className="muted">Could not load: {error}</p></div>}

      {/*
        One boundary around the routed page rather than one per page.

        The fallback matches what this component already shows while a season
        is resolving, so moving between tabs looks the same whether the page's
        code is already in hand or still arriving. The header, the nav and the
        footer stay on screen throughout — only the panel changes.
      */}
      <Suspense
        fallback={
          <section className="card">
            <p className="muted">Loading…</p>
          </section>
        }
      >
        {page}
      </Suspense>

      <InstallPrompt />

      <footer className="foot">
        <SponsorStrip seasonId={seasonId} league={league} />
        {season?.archived && (
          <p className="muted">
            {season.name} is an archived record, shown exactly as played under the scoring system
            of the time. It is never recalculated.
          </p>
        )}
      </footer>

      <nav className="tabbar" aria-label="League sections">
        {navItems(league?.unitNoun ?? 'Teams', hasNightTab, hasMvpTab).map((item) => {
          const href = item.path ? `${base}/${item.path}` : base;
          const active = section === item.path;
          return (
            <a
              key={item.path}
              href={href}
              className={active ? 'tab active' : 'tab'}
              aria-current={active ? 'page' : undefined}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={THEME_ICONS[league?.theme ?? '']?.[item.path] ?? NAV_ICONS[item.path]} />
              </svg>
              <span>{item.label}</span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}
